import React from 'react';
import { motion } from 'framer-motion';
import { LightBulbIcon, BeakerIcon, BugAntIcon, CloudIcon, HomeModernIcon, CubeTransparentIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';

const Solutions = () => {
    const solutionsList = [
        {
            title: "Crop Advisory & Agronomy",
            description: "Expert guidance on crop selection, cultivation practices, and yield enhancement tailored to your region.",
            image: "https://images.unsplash.com/photo-1595837648356-832f05786d14?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
            icon: LightBulbIcon
        },
        {
            title: "Soil Health & Fertilizer Management",
            description: "Comprehensive soil testing and customized fertilizer plans to restore soil fertility and boost productivity.",
            image: "https://images.unsplash.com/photo-1464226184884-fa280b87c399?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
            icon: BeakerIcon
        },
        {
            title: "Pest & Disease Management",
            description: "Early detection and integrated pest management strategies to minimize crop loss and ensure healthy produce.",
            image: "https://images.unsplash.com/photo-1543714574-8b83594b293d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
            icon: BugAntIcon
        },
        {
            title: "Smart Irrigation",
            description: "IoT-enabled irrigation systems that optimize water usage based on soil moisture and weather conditions.",
            image: "https://images.unsplash.com/photo-1563514227147-6d2ff665a6a0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
            icon: CloudIcon
        },
        {
            title: "Precision & Protected Farming",
            description: "High-tech farming in controlled environments like greenhouses to grow high-value crops year-round.",
            image: "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
            icon: HomeModernIcon
        },
        {
            title: "Hydroponics",
            description: "Soilless farming solutions for urban and resource-scarce environments, maximizing yield with minimal water.",
            image: "https://images.unsplash.com/photo-1556736486-1d11340b6e92?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
            icon: CubeTransparentIcon
        },
        {
            title: "Post-Harvest Solutions",
            description: "Strategies and technologies to reduce post-harvest losses and improve the shelf life of your produce.",
            image: "https://images.unsplash.com/photo-1622384738590-798836569eb4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
            icon: ArchiveBoxIcon
        }
    ];

    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15
            }
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-indigo-900/80 z-10"></div>
                    <img
                        src="https://images.unsplash.com/photo-1595837648356-832f05786d14?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80"
                        alt="Solutions Hero"
                        className="w-full h-full object-cover opacity-60"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-4xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-7xl font-thin mb-6"
                    >
                        Our <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">Solutions</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-lg md:text-xl text-gray-300 font-light max-w-2xl mx-auto"
                    >
                        Smart, sustainable & scalable agriculture solutions designed to meet the challenges of modern farming.
                    </motion.p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={staggerContainer}
                    className="space-y-20"
                >
                    {solutionsList.map((solution, index) => (
                        <motion.div
                            key={index}
                            variants={fadeInUp}
                            className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 group`}
                        >
                            <div className="lg:w-1/2 w-full relative">
                                <div className="absolute inset-0 bg-indigo-600 rounded-3xl -rotate-6 scale-95 opacity-20 group-hover:rotate-0 transition-transform duration-500"></div>
                                <img src={solution.image} alt={solution.title} className="rounded-3xl shadow-2xl w-full h-80 object-cover relative z-10 transform transition-transform duration-500 group-hover:scale-[1.02]" />
                            </div>

                            <div className="lg:w-1/2 w-full text-left">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600">
                                        <solution.icon className="h-8 w-8" />
                                    </div>
                                    <span className="text-sm font-bold text-indigo-900 uppercase tracking-widest opacity-60">Solution 0{index + 1}</span>
                                </div>
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{solution.title}</h2>
                                <p className="text-gray-600 text-lg leading-relaxed font-light mb-8">{solution.description}</p>
                                <button className="text-primary font-bold uppercase tracking-widest text-sm hover:text-indigo-600 transition-colors">
                                    Discover More &rarr;
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </div>
    );
};

export default Solutions;
